import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Search, Plus, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * TemplatePicker — a KiviHealth-style filter list next to any free-text
 * consultation field.  Doctors type 2-3 letters, the list narrows, and
 * clicking a template either appends it (mode=append, chip style) or
 * replaces the value (mode=replace).  Every click bumps the template's
 * usage_count so the most-used phrases float to the top.
 *
 * Props:
 *   type       — one of "complaint" | "history" | "examination" |
 *                "diagnosis" | "advice" | "follow_up" | "treatment"
 *   value      — the current textarea/input value
 *   onChange(v)— setter
 *   mode       — "append" (default, joins with "\n") or "replace"
 *   testid     — data-testid prefix for tests
 */
export default function TemplatePicker({ type, value = "", onChange, mode = "append", testid = "tpl" }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const debounce = useRef(null);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/rx-templates", { params: { type, q: query, limit: 80 } });
      setItems(data || []);
    } catch (e) {
      // silent — templates are optional
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load("");
    // Reload when type changes; empty q loads top-popular ones
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(q), 150);
    return () => clearTimeout(debounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const pick = async (t) => {
    if (mode === "replace") {
      onChange?.(t.text);
    } else {
      const cur = (value || "").trim();
      // Avoid appending an exact duplicate line
      const already = cur.split("\n").some((l) => l.trim().toLowerCase() === t.text.trim().toLowerCase());
      onChange?.(already ? cur : (cur ? `${cur}\n${t.text}` : t.text));
    }
    // Bump usage_count in the background
    api.post(`/rx-templates/${t.id}/use`).catch(() => {});
  };

  const addNew = async () => {
    const text = newText.trim();
    if (!text) return;
    try {
      const { data } = await api.post("/rx-templates", { type, text });
      setItems((it) => [data, ...it.filter((x) => x.id !== data.id)]);
      setNewText("");
      setAdding(false);
      toast.success("Template saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="border border-slate-200 rounded-sm bg-slate-50/50" data-testid={`${testid}-picker`}>
      <div className="flex items-center gap-2 px-2 py-1.5 border-b bg-white">
        <div className="relative flex-1">
          <Search className="w-3 h-3 absolute left-2 top-2 text-slate-400"/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type to filter templates"
            className="w-full pl-6 pr-2 h-6 text-xs border border-slate-200 rounded-sm bg-white focus:outline-none focus:border-blue-400"
            data-testid={`${testid}-filter`}
          />
        </div>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="text-[11px] text-blue-700 hover:underline inline-flex items-center gap-0.5"
          data-testid={`${testid}-add-toggle`}
        >
          <Plus className="w-3 h-3"/> Add new
        </button>
      </div>

      {adding && (
        <div className="flex items-center gap-1 px-2 py-1.5 border-b bg-blue-50/60">
          <input
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder={`New ${type} template…`}
            className="flex-1 h-6 text-xs px-1.5 border border-slate-300 rounded-sm bg-white"
            autoFocus
            data-testid={`${testid}-add-input`}
            onKeyDown={(e) => { if (e.key === "Enter") addNew(); if (e.key === "Escape") { setAdding(false); setNewText(""); } }}
          />
          <button type="button" onClick={addNew} className="text-[11px] px-2 py-0.5 bg-blue-700 text-white rounded-sm hover:bg-blue-800" data-testid={`${testid}-add-save`}>Save</button>
          <button type="button" onClick={() => { setAdding(false); setNewText(""); }} className="text-[11px] px-1 py-0.5 text-slate-500 hover:bg-slate-100 rounded-sm"><X className="w-3 h-3"/></button>
        </div>
      )}

      <div className="max-h-36 overflow-y-auto text-xs">
        {loading && filtered.length === 0 ? (
          <div className="p-2 text-slate-400 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-2 text-slate-400 text-center flex items-center justify-center gap-1">
            <Sparkles className="w-3 h-3"/> No templates. Click <b>Add new</b> to save your first one.
          </div>
        ) : (
          filtered.map((t) => (
            <button
              type="button"
              key={t.id}
              onClick={() => pick(t)}
              className="w-full text-left px-2 py-1 border-b last:border-b-0 hover:bg-blue-50 hover:text-blue-900 truncate"
              data-testid={`${testid}-item-${t.id}`}
              title="Click to insert"
            >
              {t.text}
              {t.usage_count > 0 && (
                <span className="text-[9px] text-slate-400 ml-2">×{t.usage_count}</span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
